import { matchPath } from 'react-router';
import type { TreeNode } from '@morph-mapper/types';
import type { ChatIntent } from '@/store';

const EDITORS: Record<string, string> = {
  graph: 'graph editor',
  code: 'code editor',
  context: 'context editor',
};

/** A field's name as the template summary shows it, e.g. "Invoice.debtor". */
export const fieldPath = (
  entries: Record<string, TreeNode>,
  id: string
): string | undefined => {
  const names: string[] = [];
  const seen = new Set<string>();
  let current: TreeNode | undefined = entries[id];
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.name);
    current = current.parentId ? entries[current.parentId] : undefined;
  }
  return names.length > 0 ? names.join('.') : undefined;
};

/**
 * The field whose editor is open, for the mapping assistant, e.g.
 * "Invoice.debtor (graph editor)". Only the graph, code and context editors
 * show in the URL; a cell or value edited in the list doesn't.
 */
export const openFieldFor = (
  intent: ChatIntent | null,
  pathname: string,
  entries: Record<string, TreeNode>
): string | undefined => {
  if (intent !== 'mapping-assistant') return undefined;

  const match = matchPath('/:templateId/entries/:entryId/:editor/*', pathname);
  const entryId = match?.params.entryId;
  const editor = match?.params.editor;
  if (!entryId || !editor || !Object.hasOwn(EDITORS, editor)) return undefined;

  const path = fieldPath(entries, entryId);
  return path ? `${path} (${EDITORS[editor]})` : undefined;
};

/** The id of the field whose editor is open, read from the URL. */
export const openEntryIdFor = (pathname: string): string | undefined =>
  matchPath('/:templateId/entries/:entryId/*', pathname)?.params.entryId;
